import { supabase } from "@/integrations/supabase/client";
;
import { 
  Employee, 
  Shift, 
  UpdateShiftParams, 
  ShiftAssignmentResult,
  ShiftUpdateResponse 
} from '@/types/shifts';
import { getTodayDate } from '@/utils/shiftUtils';
import { EmployeeStatus } from '@/contexts/employees/EmployeesContext';

/**
 * Service for managing shift-related operations
 */
export class ShiftService {
  /**
   * Fetch all employees for a company
   */
  static async fetchEmployees(companyId: string): Promise<Employee[]> {
    try {
      
      
      const { data, error } = await supabase
        .from('employees')
        .select('*')
        .eq('company_id', companyId)
        .order('first_name', { ascending: true })
        .order('last_name', { ascending: true });

      if (error) {
        throw new Error(`Failed to fetch employees: ${error.message}`);
      }

      // Transform to Employee interface
      const employees: Employee[] = (data || []).map(emp => ({
        id: emp.id,
        name: `${emp.first_name || ''} ${emp.last_name || ''}`.trim(),
        email: emp.email || '',
        department: emp.department || '',
        position: emp.position || '',
        status: emp.status || EmployeeStatus.ACTIVE,
        companyId: emp.company_id,
        employee_id: emp.employee_id || '',
        is_duty_roster: emp.is_duty_roster || false // ✅ NEW: Include duty roster field
      }));

      

      return employees;
    } catch {
      // Re-throw the error
    }
  }

  /**
   * Fetch current active shift assignment for all employees in a company
   * Returns a map of employee_id -> { shift_id, name, code }
   */
  static async fetchCurrentAssignments(companyId: string): Promise<Map<string, { shift_id: string; name: string; code: string | null }>> {
    try {
      const { data, error } = await supabase
        .from('employee_shifts')
        .select(`
          employee_id,
          shift_id,
          company_id,
          is_active,
          shifts ( id, name, code )
        `)
        .eq('company_id', companyId)
        .eq('is_active', true);

      if (error) {
        throw new Error(`Failed to fetch current assignments: ${error.message}`);
      }

      const map = new Map<string, { shift_id: string; name: string; code: string | null }>();
      type ShiftRel = { id: string; name: string; code: string | null };
      type Row = { employee_id: string; shift_id: string; shifts?: ShiftRel | ShiftRel[] };
      const typedRows = (data || []) as unknown as Row[];
      for (const row of typedRows) {
        let name = '';
        let code: string | null = null;
        const rel = row.shifts as unknown;
        if (Array.isArray(rel)) {
          if (rel.length > 0) {
            name = (rel[0] as ShiftRel).name ?? '';
            code = (rel[0] as ShiftRel).code ?? null;
          }
        } else if (rel && typeof rel === 'object') {
          name = (rel as ShiftRel).name ?? '';
          code = (rel as ShiftRel).code ?? null;
        }
        map.set(row.employee_id, {
          shift_id: row.shift_id,
          name,
          code,
        });
      }

      return map;
    } catch {
      // Re-throw the error
    }
  }
  /**
   * Fetch all shifts for a company
   */
  static async fetchShifts(companyId: string): Promise<Shift[]> {
    try {
      
      
      const { data, error } = await supabase
        .from('shifts')
        .select('*')
        .eq('company_id', companyId)
        .eq('is_del', false)
        .eq('is_active', true)
        .order('name')
        .order('code');

      if (error) {
        throw new Error(`Failed to fetch shifts: ${error.message}`);
      }

      const shifts: Shift[] = (data || []).map(shift => ({
        id: shift.id,
        name: shift.name,
        code: shift.code,
        valid_from: shift.valid_from,
        day_time_deduction: shift.day_time_deduction || [],
        company_id: shift.company_id,
        is_active: shift.is_active,
        is_del: shift.is_del,
        in_working_hours: shift.in_working_hours
      }));

      

      return shifts;
    } catch {
      // Re-throw the error
    }
  }

  /**
   * Assign a shift to an employee using the RPC function
   */
  static async assignEmployeeShift(params: UpdateShiftParams): Promise<ShiftAssignmentResult> {
    try {
      

      const { error } = await supabase.rpc('assign_employee_shift', {
        p_employee_id: params.employee_id,
        p_shift_id: params.shift_id,
        p_from: params.from_date,
        p_to: params.to_date,
        p_company_id: params.company_id
      });

      if (error) {
        return {
          success: false,
          employee_id: params.employee_id,
          error: error.message
        };
      }

      

      return {
        success: true,
        employee_id: params.employee_id
      };
    } catch {
      return {
        success: false,
        employee_id: params.employee_id,
        error: 'Unknown error occurred'
      };
    }
  }

  /**
   * Bulk assign shifts to multiple employees
   */
  static async bulkAssignShifts(
    employeeIds: string[],
    shiftId: string,
    companyId: string
  ): Promise<ShiftUpdateResponse> {
    const today = getTodayDate();
    const results: ShiftAssignmentResult[] = [];

    

    // Process each employee sequentially to avoid overwhelming the database
    for (const employeeId of employeeIds) {
      const result = await this.assignEmployeeShift({
        employee_id: employeeId,
        shift_id: shiftId,
        from_date: today,
        to_date: 'infinity',
        company_id: companyId
      });
      
      results.push(result);
      
      // Small delay between requests to be respectful to the database
      await new Promise(resolve => setTimeout(resolve, 100));
    }

    const successCount = results.filter(r => r.success).length;
    const failureCount = results.filter(r => !r.success).length;

    

    return {
      success: failureCount === 0,
      results,
      totalCount: employeeIds.length,
      successCount,
      failureCount
    };
  }

  /**
   * Get employee shift history
   */
  static async getEmployeeShiftHistory(employeeId: string): Promise<unknown[]> {
    try {
      const { data, error } = await supabase
        .from('employee_shifts')
        .select(`
          id,
          assigned_from,
          assigned_to,
          is_active,
          shift_id,
          shifts (
            id, 
            name,
            day_time_deduction
          )
        `)
        .eq('employee_id', employeeId)
        .order('assigned_from', { ascending: false });

      if (error) {
        throw new Error(`Failed to fetch shift history: ${error.message}`);
      }

      return data || [];
    } catch {
      // Re-throw the error
    }
  }

  /**
   * Get current shift assignment for an employee
   */
  static async getCurrentShiftAssignment(employeeId: string): Promise<unknown | null> {
    try {
      const { data, error } = await supabase
        .from('employee_shifts')
        .select(`
          id,
          assigned_from,
          assigned_to,
          is_active,
          shift_id,
          shifts (
            id, 
            name,
            day_time_deduction
          )
        `)
        .eq('employee_id', employeeId)
        .eq('is_active', true)
        .single();

      if (error && error.code !== 'PGRST116') {
        throw new Error(`Failed to fetch current shift assignment: ${error.message}`);
      }

      return data;
    } catch {
      // Re-throw the error
    }
  }

  /**
   * Validate shift assignment parameters
   */
  static validateShiftAssignment(
    employeeId: string,
    shiftId: string,
    companyId: string
  ): { isValid: boolean; errors: string[] } {
    const errors: string[] = [];

    if (!employeeId) {
      errors.push('Employee ID is required');
    }

    if (!shiftId) {
      errors.push('Shift ID is required');
    }

    if (!companyId) {
      errors.push('Company ID is required');
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  }
} 